import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthFlowService } from '../services/auth-flow.service';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthFlowGuard implements CanActivate {
  constructor(
    private authFlowService: AuthFlowService,
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Check if user is already authenticated
    const isAuthenticated = this.authService.isAuthenticatedSync();
    if (isAuthenticated) {
      // Already logged in, redirect to home
      return this.router.createUrlTree(['/home']);
    }

    // Get the required step from route data
    const requiredStep = route.data['requiredStep'];
    const flowState = this.authFlowService.getFlowState();

    // If no flow state exists
    if (!flowState) {
      // For login page, allow access
      if (requiredStep === 'login') {
        return true;
      }
      // Verify with no flow is the cold-open case. Tell login why.
      if (requiredStep === 'verify') {
        return this.router.createUrlTree(['/login'], {
          queryParams: { notice: 'auth-session-expired' }
        });
      }
      // For other pages, redirect to login
      return this.router.createUrlTree(['/login']);
    }

    // Check if the current step matches the required step
    if (flowState.currentStep !== requiredStep) {
      // Redirect based on current step
      switch (flowState.currentStep) {
        case 'login':
          return this.router.createUrlTree(['/login']);
        case 'verify':
          return this.router.createUrlTree(['/verify']);
        case 'device-verify':
          return this.router.createUrlTree(['/device-verify']);
        case 'factor-setup':
          return this.router.createUrlTree(['/factor-setup']);
        default:
          return this.router.createUrlTree(['/login']);
      }
    }

    // Additional validation for specific steps
    switch (requiredStep) {
      case 'verify':
        // Must have email and auth mode
        if (!flowState.email || !flowState.authMode) {
          return this.router.createUrlTree(['/login'], {
            queryParams: { notice: 'auth-session-expired' }
          });
        }
        break;
      
      case 'device-verify':
        // Must have completed primary auth
        if (!flowState.email || !flowState.requestId) {
          return this.router.createUrlTree(['/login']);
        }
        break;

      case 'factor-setup':
        // Must have email and requestId (primary auth completed, no secondary factors)
        if (!flowState.email || !flowState.requestId) {
          return this.router.createUrlTree(['/login']);
        }
        break;
    }

    // All checks passed
    return true;
  }
}
